import { motion } from 'framer-motion';
import { Calendar, ChevronRight, Clock, Headphones, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { EventWithTasks } from '@/hooks/use-events';
import { ease } from '@/lib/motion';
import {
  fmtCouvert,
  fmtDateBR,
  fmtTime,
  fmtWeekday,
  fmtWeekdayLong,
  parseDateOnly,
  todayISO,
} from '@/lib/dates';

interface EventCardProps {
  event: EventWithTasks;
  onClick?: () => void;
}

/* ── Helpers locais ──────────────────────────────────────────────── */

// Linha de meta com ícone + texto mono uppercase (igual MetaInline do protótipo).
function MetaInline({
  icon: Icon,
  children,
}: {
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3 w-3 shrink-0" />
      {children}
    </span>
  );
}

// Barra de progresso de tasks: 100% => verde, senão vermelho primary.
function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <span className="inline-block h-1 w-14 overflow-hidden rounded-full bg-surface-2">
      <span
        className={`block h-full rounded-full transition-[width] duration-300 ${
          pct === 100 ? 'bg-success' : 'bg-primary'
        }`}
        style={{ width: `${pct}%` }}
      />
    </span>
  );
}

// Calcula dias até a data do evento (inline, conforme padrão do projeto).
function daysUntil(eventDate: string): number {
  const d = parseDateOnly(eventDate);
  const t = parseDateOnly(todayISO());
  return Math.round((d.getTime() - t.getTime()) / 86_400_000);
}

/* ── EventCard ───────────────────────────────────────────────────── */

export function EventCard({ event, onClick }: EventCardProps) {
  const days = daysUntil(event.event_date);
  const isToday = days === 0;
  const isSoon = days > 0 && days <= 7;
  const isDraft = event.status === 'draft';

  const lineup = (event.attractions ?? [])
    .filter((a) => a.name.trim())
    .map((a) => a.name)
    .join(' · ');

  const tasksTotal = event.tasks.length;
  const tasksDone = event.tasks.filter((tk) => tk.status === 'done').length;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2, boxShadow: 'var(--shadow-card)' }}
      whileTap={{ scale: 0.99 }}
      transition={{ duration: 0.18, ease }}
      className="flex w-full flex-col gap-3.5 rounded-2xl border border-border bg-card p-5 text-left shadow-elev"
    >
      {/* Topo: status + horário */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {isToday ? (
            <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
              Hoje
            </span>
          ) : isSoon ? (
            <span className="inline-flex items-center rounded-full bg-primary-soft px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
              Em {days}d
            </span>
          ) : isDraft ? (
            <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Rascunho
            </span>
          ) : (
            <span className="eyebrow">
              {fmtWeekday(event.event_date)} · {fmtDateBR(event.event_date)}
            </span>
          )}
        </div>
        {event.open_time && (
          <span className="font-mono text-xs font-medium tracking-wide text-muted-foreground">
            {fmtTime(event.open_time)} – {fmtTime(event.close_time) || '—'}
          </span>
        )}
      </div>

      {/* Título */}
      <h3 className="u-event-title text-[18px] text-foreground">{event.name}</h3>

      {/* Linhas de meta */}
      <div className="flex flex-col gap-1.5">
        {(isToday || isSoon || isDraft) && (
          <MetaInline icon={Calendar}>{fmtWeekdayLong(event.event_date)}</MetaInline>
        )}
        {lineup && <MetaInline icon={Headphones}>{lineup}</MetaInline>}
        <MetaInline icon={Wallet}>
          {fmtCouvert(event.couvert_type, event.couvert_value, event.couvert_until)}
        </MetaInline>
      </div>

      {/* Rodapé de tasks (só se houver) */}
      {tasksTotal > 0 && (
        <div className="flex items-center justify-between border-t border-border pt-3">
          <span className="eyebrow">tasks</span>
          <span className="inline-flex items-center gap-2">
            <ProgressBar value={tasksDone} max={tasksTotal} />
            <span className="font-mono text-[11px] font-semibold text-foreground">
              {tasksDone}/{tasksTotal}
            </span>
          </span>
        </div>
      )}
    </motion.button>
  );
}

/* ── EventRow — linha compacta (modo "lista" do dashboard) ───────── */

export function EventRow({ event, onClick }: EventCardProps) {
  const days = daysUntil(event.event_date);
  const isToday = days === 0;
  const isSoon = days > 0 && days <= 7;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 border-b border-border bg-card px-4 py-3.5 text-left"
    >
      {/* Bloco de data quadrado */}
      <div
        className={`flex min-w-11 flex-col items-center justify-center rounded-md py-1.5 ${
          isToday ? 'bg-primary text-primary-foreground' : 'bg-surface-2 text-foreground'
        }`}
      >
        <span className="font-mono text-[9px] uppercase tracking-wide opacity-80">
          {fmtWeekday(event.event_date)}
        </span>
        <span className="font-display text-[18px] font-bold leading-none">
          {parseDateOnly(event.event_date).getDate()}
        </span>
      </div>

      {/* Nome + meta */}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-display text-sm font-bold uppercase tracking-tight text-foreground">
          {event.name}
        </span>
        <span className="mt-1 flex items-center gap-2.5">
          {event.open_time && <MetaInline icon={Clock}>{fmtTime(event.open_time)}</MetaInline>}
          <MetaInline icon={Wallet}>
            {fmtCouvert(event.couvert_type, event.couvert_value, event.couvert_until)}
          </MetaInline>
        </span>
      </span>

      {/* Badge + chevron */}
      {isSoon && !isToday && (
        <span className="inline-flex items-center rounded-full bg-primary-soft px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
          {days}d
        </span>
      )}
      {isToday && (
        <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
          Hoje
        </span>
      )}
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}
